/**
 * A `/goal`: the agent keeps working, iteration after iteration, until an independent judge
 * agrees the goal is met.
 *
 * Two sources describe it. The live `goal` event carries its status while a loop runs; the
 * thread's goal notes — user turns with `origin: 'goal'`, the transcript the loop reads its goal
 * back from — say the same thing after a reload with no loop attached. Each note is
 * `<tag>\n<text for the model>\n<JSON>\n</tag>`, and the JSON line is all this module reads.
 */

export type GoalPhase = 'running' | 'paused' | 'ended';

/** Why a goal paused or ended — the Rust `PauseReason` / `EndReason`. */
export interface GoalReason {
  kind: string;
  /** A failed call's or an unreachable judge's message. */
  message?: string;
  /** A met goal's summary, from the judge. */
  summary?: string;
}

/** What the `goal` event carries. */
export interface GoalStatus {
  objective: string;
  phase: GoalPhase;
  reason?: GoalReason;
  iteration: number;
  maxIterations: number;
  spentMicroUsd: number;
  budgetMicroUsd?: number;
  interactive: boolean;
  judging: boolean;
}

interface NoteCounters {
  iteration: number;
  spent_micro_usd: number;
}

interface NoteSpec {
  objective: string;
  max_iterations: number;
  budget_micro_usd: number | null;
  interactive: boolean;
}

/** One goal note's JSON line — the Rust `Note`. */
export type GoalNote =
  | { note: 'started'; spec: NoteSpec; counters: NoteCounters }
  | { note: 'continued'; counters: NoteCounters }
  | { note: 'paused'; reason: GoalReason; counters: NoteCounters }
  | { note: 'resumed'; counters: NoteCounters }
  | { note: 'ended'; reason: GoalReason; counters: NoteCounters };

const NOTES: readonly GoalNote['note'][] = ['started', 'continued', 'paused', 'resumed', 'ended'];

/** The note a goal turn's content carries, or `null` when it is not one. */
export function parseGoalNote(content: string): GoalNote | null {
  if (!content.startsWith('<goal')) return null;
  const lines = content.trimEnd().split('\n');
  if (lines.length < 2 || !lines[lines.length - 1]!.startsWith('</goal')) return null;
  try {
    const parsed: unknown = JSON.parse(lines[lines.length - 2]!);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const note = (parsed as { note?: unknown }).note;
    return NOTES.includes(note as GoalNote['note']) ? (parsed as GoalNote) : null;
  } catch {
    return null;
  }
}

/** The goal `notes` describe, newest last; `null` without a start among them. */
export function foldGoal(notes: GoalNote[]): GoalStatus | null {
  let goal: GoalStatus | null = null;
  for (const note of notes) {
    if (note.note === 'started') {
      goal = {
        objective: note.spec.objective,
        phase: 'running',
        iteration: note.counters.iteration,
        maxIterations: note.spec.max_iterations,
        spentMicroUsd: note.counters.spent_micro_usd,
        budgetMicroUsd: note.spec.budget_micro_usd ?? undefined,
        interactive: note.spec.interactive,
        judging: false,
      };
      continue;
    }
    if (!goal) continue;
    const counters = { iteration: note.counters.iteration, spentMicroUsd: note.counters.spent_micro_usd };
    switch (note.note) {
      case 'continued':
      case 'resumed':
        goal = { ...goal, ...counters, phase: 'running', reason: undefined };
        break;
      case 'paused':
        goal = { ...goal, ...counters, phase: 'paused', reason: note.reason };
        break;
      case 'ended':
        goal = { ...goal, ...counters, phase: 'ended', reason: note.reason };
        break;
    }
  }
  return goal;
}

/** The goal a thread's goal turns describe. */
export function deriveGoal(turns: { role: string; content: string }[]): GoalStatus | null {
  const notes = turns
    .filter((turn) => turn.role === 'goal')
    .map((turn) => parseGoalNote(turn.content))
    .filter((note): note is GoalNote => note !== null);
  return foldGoal(notes);
}

export function reasonLabel(reason: GoalReason | undefined): string {
  switch (reason?.kind) {
    case 'aborted':
      return 'stopped by you';
    case 'failed':
      return reason.message ? `a model call failed: ${reason.message}` : 'a model call failed';
    case 'judge_unavailable':
      return 'the judge could not be reached';
    case 'session_resumed':
      return 'the session was reopened';
    case 'met':
      return 'met';
    case 'iteration_cap':
      return 'iteration cap reached';
    case 'no_progress':
      return 'no progress in several iterations';
    case 'budget':
      return 'budget spent';
    case 'stopped':
      return 'stopped';
    default:
      return '';
  }
}

/** The thread's one-line notice for a goal note. */
export function goalNoteLabel(note: GoalNote): string {
  switch (note.note) {
    case 'started':
      return `Goal: ${note.spec.objective}`;
    case 'continued':
      return `Goal · iteration ${note.counters.iteration}`;
    case 'paused':
      return `Goal paused — ${reasonLabel(note.reason)}`;
    case 'resumed':
      return 'Goal resumed';
    case 'ended':
      return note.reason.kind === 'met'
        ? `Goal met — ${note.reason.summary ?? ''}`.trimEnd()
        : `Goal ended — ${reasonLabel(note.reason)}`;
  }
}

/** Dollars to the cent, from micro-dollars. */
export function usd(microUsd: number): string {
  return `$${(microUsd / 1_000_000).toFixed(2)}`;
}

/** The `goal_start` frame, beside its `type` — the loop's own `GoalStart` command. */
export interface GoalStartRequest {
  objective: string;
  budgetMicroUsd?: number;
  maxIterations?: number;
  /** Whether a question may pause the goal for the user ("Ask me when unsure"). */
  interactive: boolean;
}

/** What a typed `/goal …` line asks for. */
export type GoalCommand =
  | { kind: 'start'; objective: string; budgetMicroUsd?: number; maxIterations?: number }
  | { kind: 'stop' }
  | { kind: 'resume' }
  | { kind: 'invalid'; message: string };

/**
 * Read a composer line as a goal command: `/goal <objective> [--budget USD] [--max N]`,
 * `/goal stop`, `/goal resume`. `null` when the line is not a `/goal` at all.
 */
export function parseGoalCommand(line: string): GoalCommand | null {
  const match = /^\/goal(?:\s+([\s\S]*))?$/.exec(line.trim());
  if (!match) return null;
  const args = (match[1] ?? '').trim();
  if (args === 'stop') return { kind: 'stop' };
  if (args === 'resume') return { kind: 'resume' };

  const words = args.split(/\s+/).filter(Boolean);
  const objective: string[] = [];
  let budgetMicroUsd: number | undefined;
  let maxIterations: number | undefined;
  for (let i = 0; i < words.length; i++) {
    const word = words[i]!;
    if (word === '--budget') {
      const value = words[++i] ?? '';
      const dollars = Number(value.replace(/^\$/, ''));
      if (!Number.isFinite(dollars) || dollars <= 0) {
        return { kind: 'invalid', message: `--budget needs an amount in dollars, got “${value}”` };
      }
      budgetMicroUsd = Math.round(dollars * 1_000_000);
    } else if (word === '--max') {
      const value = words[++i] ?? '';
      const max = Number(value);
      if (!Number.isInteger(max) || max <= 0) {
        return { kind: 'invalid', message: `--max needs a number of iterations, got “${value}”` };
      }
      maxIterations = max;
    } else {
      objective.push(word);
    }
  }
  if (objective.length === 0) {
    return { kind: 'invalid', message: '/goal needs an objective: what should be true when it is done' };
  }
  return { kind: 'start', objective: objective.join(' '), budgetMicroUsd, maxIterations };
}
